import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Flame, MessageCircle, Radio, Users } from 'lucide-react';
import { GlassCard } from '@/components/shared/GlassCard';
import { Pulse } from '@/components/shared/Pulse';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { Button } from '@/components/ui/button';
import { brand } from '@/lib/theme/tokens';
import { photo, unsplash } from '@/lib/utils/img';
import { fadeUp, inViewOnce, staggerContainer } from '@/lib/motion/variants';
import { useClubsLeaderboard } from '@/features/marketplace/hooks/useClubs';
import { useCompanionDeck } from '@/features/marketplace/hooks/useCompanions';
import { useTrailConnectPulse } from '@/features/marketplace/hooks/useTrailConnect';

export function TrailConnectPromo() {
  const { data: leaderboard = [] } = useClubsLeaderboard({ period: 'week' });
  const topStreakers = leaderboard.slice(0, 4);

  const { data: companionDeck = [] } = useCompanionDeck();
  const featuredCompanions = companionDeck.slice(0, 3);

  const { data: pulse } = useTrailConnectPulse();
  const liveClubCount = pulse?.active_clubs ?? 20;

  return (
    <section className="mx-auto mt-24 max-w-7xl px-4 md:px-6">
      <SectionHeader
        kicker="TrailConnect · the social layer"
        kickerColor={brand.chinar}
        title={
          <>
            The trail is more fun{' '}
            <em
              style={{
                display: 'inline-block',
                lineHeight: 1.1,
                paddingBottom: '0.1em',
                backgroundImage: `linear-gradient(135deg, ${brand.chinar}, ${brand.saffron})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
                fontStyle: 'italic',
              }}
            >
              with company.
            </em>
          </>
        }
        description="Show up to clubs. Match with companions. Build streaks. Travel together."
        actions={
          <Button asChild variant="outline" size="sm" className="hidden md:inline-flex">
            <Link to="/trailconnect">
              How it works <ArrowRight size={12} />
            </Link>
          </Button>
        }
      />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={inViewOnce}
        variants={staggerContainer}
        className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3"
      >
        {/* ── Clubs card ───────────────────────────────────── */}
        <motion.div variants={fadeUp}>
          <Link to="/clubs" className="block h-full">
            <GlassCard interactive className="relative h-full overflow-hidden p-6">
              <div
                aria-hidden
                className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-50 blur-2xl"
                style={{ background: `${brand.chinar}55` }}
              />
              <div className="relative">
                <div
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em]"
                  style={{ background: `${brand.chinar}1c`, color: brand.chinar, border: `1px solid ${brand.chinar}40` }}
                >
                  <Flame size={11} /> Clubs · earn
                </div>
                <h3 className="font-display mt-3 text-2xl font-semibold leading-tight tracking-tight text-ink">
                  Show up to your{' '}
                  <em style={{ color: brand.chinar }}>87-day streak.</em>
                </h3>
                <p className="mt-2 text-[13px] leading-relaxed text-ink-dim">
                  {liveClubCount} clubs in your city. Cycling at dawn, yoga at sunrise, badminton at lunch. +24 pts per session, +100 every 7 days.
                </p>

                <ul className="mt-4 space-y-2">
                  {topStreakers.map((l) => (
                    <li
                      key={l.name}
                      className="flex items-center gap-2.5 rounded-lg border border-glass-edge bg-glass p-2"
                    >
                      <img
                        src={l.avatar_url ?? ''}
                        alt=""
                        aria-hidden
                        loading="lazy"
                        decoding="async"
                        className="h-7 w-7 rounded-full object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[12px] font-semibold text-ink">{l.name}</div>
                        <div className="text-[11px] text-ink-mute">
                          {l.top_activity} · {l.city}
                        </div>
                      </div>
                      <span
                        className="inline-flex items-center gap-1 text-[11px] font-semibold"
                        style={{ color: brand.chinar }}
                      >
                        <Flame size={11} fill={brand.chinar} />
                        {l.streak}d
                      </span>
                    </li>
                  ))}
                </ul>

                <div
                  className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold"
                  style={{ color: brand.chinar }}
                >
                  Browse clubs <ArrowRight size={12} />
                </div>
              </div>
            </GlassCard>
          </Link>
        </motion.div>

        {/* ── Community card ──────────────────────────────── */}
        <motion.div variants={fadeUp}>
          <Link to="/community" className="block h-full">
            <GlassCard interactive className="relative h-full overflow-hidden p-6">
              <div
                aria-hidden
                className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-50 blur-2xl"
                style={{ background: `${brand.teal}55` }}
              />
              <div className="relative">
                <div
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em]"
                  style={{ background: `${brand.teal}1c`, color: brand.teal, border: `1px solid ${brand.teal}40` }}
                >
                  <Users size={11} /> Community · match
                </div>
                <h3 className="font-display mt-3 text-2xl font-semibold leading-tight tracking-tight text-ink">
                  Find people doing{' '}
                  <em style={{ color: brand.teal }}>your trip.</em>
                </h3>
                <p className="mt-2 text-[13px] leading-relaxed text-ink-dim">
                  Swipe-match companions with verified club streaks. Form trip groups. Split logistics, photos, the pass-day rope team.
                </p>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  {featuredCompanions.map((c) => (
                    <div
                      key={c.tourist_id}
                      className="relative overflow-hidden rounded-xl border border-glass-edge"
                    >
                      <div className="relative aspect-[3/4]">
                        <img
                          src={c.avatar_url ?? ''}
                          alt={c.name}
                          loading="lazy"
                          decoding="async"
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
                        <div className="absolute inset-x-1.5 bottom-1.5 text-white">
                          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/85">
                            {c.match_pct ?? 0}% match
                          </div>
                          <div className="truncate text-[11px] font-semibold">{c.name}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold"
                  style={{ color: brand.teal }}
                >
                  See companions <ArrowRight size={12} />
                </div>
              </div>
            </GlassCard>
          </Link>
        </motion.div>

        {/* ── Live expedition card ────────────────────────── */}
        <motion.div variants={fadeUp}>
          <Link to="/community" className="block h-full">
            <GlassCard interactive className="relative h-full overflow-hidden p-0">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={unsplash(photo.alpineRidge, { w: 800 })}
                  alt=""
                  aria-hidden
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
                <div className="absolute left-3 top-3">
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white"
                    style={{ background: `${brand.chinar}E0`, boxShadow: `0 8px 22px ${brand.chinar}55` }}
                  >
                    <Radio size={11} /> Live now
                  </span>
                </div>
                <div className="absolute inset-x-4 bottom-4 text-white">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/85">
                    Tarsar Marsar · Day 4
                  </div>
                  <div className="font-display mt-1 text-2xl font-semibold leading-tight">
                    Virtual join the squad.
                  </div>
                  <div className="mt-2 inline-flex items-center gap-2 text-[11px] text-white/85">
                    <Pulse color={brand.ok} size={5} />
                    1,247 watching · 12,400 ft
                  </div>
                </div>
              </div>
              <div className="p-5">
                <p className="text-[13px] leading-relaxed text-ink-dim">
                  Drop into live expeditions in progress. GPS pings, photo feed, send cheers, ask the guide questions in real time.
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-semibold"
                    style={{ color: brand.teal }}
                  >
                    <MessageCircle size={11} /> 84 chats today
                  </span>
                  <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-ink">
                    <ArrowRight size={12} /> Open live feed
                  </span>
                </div>
              </div>
            </GlassCard>
          </Link>
        </motion.div>
      </motion.div>

      <div className="mt-6 flex flex-wrap gap-2 md:hidden">
        <Button asChild variant="outline" size="sm" className="flex-1">
          <Link to="/trailconnect">
            How it works <ArrowRight size={12} />
          </Link>
        </Button>
      </div>
    </section>
  );
}
